import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, CATEGORY_LABELS } from "../lib/api";
import { useCart } from "../context/CartContext";
import { ArrowLeft, ShieldCheck, Truck } from "lucide-react";

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addItem } = useCart();
  const [product, setProduct] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [activeImg, setActiveImg] = useState(0);

  useEffect(() => {
    api.get(`/products/${id}`).then(({ data }) => { setProduct(data); setActiveImg(0); }).catch(() => setNotFound(true));
  }, [id]);

  if (notFound) {
    return (
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-28 text-center">
        <h1 className="font-display font-black text-3xl text-[#262626]">Product not found</h1>
        <Link to="/products" className="inline-block mt-6 text-[#D32F2F] font-bold">← Back to shop</Link>
      </div>
    );
  }

  if (!product) return <div className="h-[60vh]" />;

  const gallery = product.images && product.images.length > 0 ? product.images : [product.image];

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-12 py-12" data-testid="product-detail-page">
      <button onClick={() => navigate(-1)} className="inline-flex items-center gap-2 text-sm font-semibold text-[#262626]/70 hover:text-[#D32F2F] mb-8">
        <ArrowLeft className="w-4 h-4" /> Back
      </button>
      <div className="grid md:grid-cols-2 gap-12">
        <div className="bg-white rounded-lg border border-[#262626]/10 p-6">
          <div className="aspect-square rounded-md overflow-hidden bg-[#F9F6F0]">
            <img src={gallery[activeImg]} alt={product.name} className="w-full h-full object-cover" />
          </div>
          {gallery.length > 1 && (
            <div className="mt-4 flex gap-3" data-testid="product-thumbnails">
              {gallery.map((src, i) => (
                <button
                  key={i}
                  onClick={() => setActiveImg(i)}
                  data-testid={`thumbnail-${i}`}
                  className={`w-20 h-20 rounded-md overflow-hidden bg-[#F9F6F0] border-2 transition-colors ${i === activeImg ? "border-[#D32F2F]" : "border-transparent hover:border-[#262626]/20"}`}
                >
                  <img src={src} alt={`${product.name} view ${i + 1}`} className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>
        <div>
          <span className="text-sm font-bold uppercase tracking-[0.2em] text-[#D32F2F]">{CATEGORY_LABELS[product.category]}</span>
          <h1 className="mt-3 text-3xl sm:text-4xl font-black tracking-tight text-[#262626]">{product.name}</h1>
          <p className="mt-5 font-display font-black text-4xl text-[#262626]" data-testid="detail-price">${product.price.toFixed(2)}</p>
          <p className="mt-6 text-[#262626]/75 leading-relaxed">{product.description}</p>
          <p className="mt-4 text-sm font-semibold text-[#262626]/60">
            {product.stock > 0 ? `${product.stock} in stock` : "Out of stock"}
          </p>
          <button
            onClick={() => addItem(product)}
            disabled={product.stock <= 0}
            data-testid="detail-add-to-cart"
            className="mt-8 w-full sm:w-auto bg-[#D32F2F] text-white font-bold px-10 py-4 rounded-md hover:bg-[#B71C1C] transition-all hover:-translate-y-1 disabled:opacity-50 disabled:translate-y-0"
          >
            Add to Cart
          </button>
          <div className="mt-10 space-y-3 border-t border-[#262626]/10 pt-6">
            <p className="flex items-center gap-3 text-sm text-[#262626]/70"><ShieldCheck className="w-5 h-5 text-[#D32F2F]" /> Authentic & carefully inspected</p>
            <p className="flex items-center gap-3 text-sm text-[#262626]/70"><Truck className="w-5 h-5 text-[#D32F2F]" /> Securely packed and shipped from Galt, CA</p>
          </div>
        </div>
      </div>
    </div>
  );
}
